import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useMemo, useState } from "react";
import PageLayout from "../components/page-layout";
import {
  BRAND_LIST,
  previewGroups,
  type BrandKey,
  type Preview,
} from "../utils/message-templates.constants";

/** The email itself, sandboxed and sized to its own content. */
const EmailFrame = ({ html }: { html: string }) => (
  <iframe
    title="email preview"
    srcDoc={html}
    sandbox="allow-same-origin"
    scrolling="no"
    className="w-full border-0 block"
    onLoad={(e) => {
      const frame = e.currentTarget;
      const fit = () =>
        frame.contentDocument &&
        (frame.style.height = `${frame.contentDocument.documentElement.scrollHeight}px`);
      fit();
      setTimeout(fit, 400);
    }}
  />
);

const TouchpointCard = ({ item }: { item: Preview }) => (
  <div className="rounded-2xl border border-edge bg-surface-elevated p-5 animate-fade-in">
    <div className="flex flex-wrap items-center gap-3 mb-5">
      <span className="rounded-full bg-accent px-3.5 py-1.5 text-xs font-bold tracking-wide text-white">
        {item.badge}
      </span>
      <span className="text-sm font-semibold text-light-gray">{item.key}</span>
      {item.sms && <span className="text-sm text-light-gray">· + SMS</span>}
    </div>

    <div className="flex gap-6 desktop:flex-col">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-light-gray mb-2">
          Email
        </p>
        <p className="text-[15px] text-text-primary mb-4">
          <span className="font-bold">Subject: </span>
          {item.subject}
        </p>
        <div className="max-h-[560px] overflow-y-auto rounded-xl border border-edge custom-scrollbar">
          <EmailFrame html={item.html} />
        </div>
      </div>

      {item.sms && (
        <div className="w-[340px] shrink-0 desktop:w-full">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-light-gray mb-2">
            SMS
          </p>
          <div className="rounded-2xl border border-edge bg-surface p-4">
            <p className="rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 px-4 py-3 text-[15px] leading-relaxed text-white shadow-glow-sm">
              {item.sms}
            </p>
          </div>
        </div>
      )}
    </div>
  </div>
);

const MessageTemplates = () => {
  const [value, setValue] = useState<{ brand: BrandKey; open: string }>({
    brand: "BUSINESS_COMPARE_DIRECT",
    open: "",
  });
  const set = (patch: Partial<typeof value>) => setValue((s) => ({ ...s, ...patch }));

  const groups = useMemo(() => previewGroups(value.brand), [value.brand]);

  return (
    <PageLayout pageTitle="Message Templates">
      <p className="max-w-3xl text-[15px] leading-relaxed text-light-gray">
        Every automated email and SMS, rendered exactly as it sends. Pick a brand, then open a
        group to preview each touchpoint — sample customer, £410 renewal on 15 July 2026.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        {BRAND_LIST.map((brand) => (
          <button
            key={brand.key}
            type="button"
            onClick={() => set({ brand: brand.key, open: "" })}
            className={clsx(
              "rounded-full px-6 py-2.5 text-[15px] font-semibold transition-all duration-200",
              value.brand === brand.key
                ? "bg-accent text-white shadow-glow"
                : "border border-edge bg-surface text-text-primary hover:border-accent/40",
            )}
          >
            {brand.name}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4">
        {groups.map((group) => {
          const open = value.open === group.folder;
          return (
            <div
              key={group.folder}
              className="rounded-2xl border border-edge bg-surface shadow-card overflow-hidden"
            >
              <button
                type="button"
                onClick={() => set({ open: open ? "" : group.folder })}
                className="flex w-full items-center gap-4 px-6 py-5 text-left hover:bg-active-background/50 transition-colors duration-200"
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className={clsx(
                    "text-light-gray transition-transform duration-300",
                    open && "rotate-90",
                  )}
                />
                <span className="text-[17px] font-bold text-text-primary">{group.title}</span>
                <span className="ml-auto text-sm text-light-gray">{group.meta}</span>
              </button>

              {open && (
                <div className="border-t border-edge-faint px-6 pb-6 pt-5">
                  <p className="mb-5 text-[15px] text-light-gray">{group.blurb}</p>
                  <div className="flex flex-col gap-5">
                    {group.items.map((item) => (
                      <TouchpointCard key={item.key} item={item} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </PageLayout>
  );
};

export default MessageTemplates;
